import type { RegisterStatus } from '@dashboard/contracts';

/**
 * What somebody wrote on site, held until there is signal to send it.
 *
 * ## Why the queue is visible and editable, not a Background Sync registration
 *
 * The browser offers to retry a request for us once connectivity returns. It
 * puts the pending request somewhere the person who wrote it cannot see, cannot
 * correct and cannot withdraw -- and a safety observation somebody realises
 * they got wrong, sending anyway from inside the browser, is worse than one
 * that sits on the screen saying "not sent yet".
 *
 * So the queue is ordinary data the capture screen renders, and sending is
 * something the page does.
 *
 * ## Why `localStorage` and not IndexedDB
 *
 * An observation is a few hundred bytes of text. IndexedDB earns its complexity
 * when there are blobs to hold, which is what photographs would be -- and
 * photographs are deliberately not in this first version, because a photo needs
 * the asset upload, which needs the network, which is the one thing this
 * feature assumes is missing. A queue that could hold a photo it could never
 * send would be a promise the product does not keep.
 *
 * Every read and write is guarded: a private window, cleared site data, or a
 * browser configured to refuse storage all throw rather than return nothing,
 * and a capture screen that crashed on load would lose the observation it
 * exists to keep.
 */

const STORAGE_KEY = 'dashboard.capture.observations';

/** What the phone holds, and what it will send. */
export interface QueuedObservation {
  /**
   * Generated on the device and sent as `clientRef`, which is what makes the
   * submission idempotent: a request that timed out may well have succeeded,
   * and the retry has to be able to say "this is the same one".
   */
  clientRef: string;
  projectId: string;
  title: string;
  description: string;
  /** 1-5, or null when the writer did not judge it. */
  severity: number | null;
  location: string;
  immediateAction: string;
  /** The day it was seen, which is reliably earlier than the day it sends. */
  observedOn: string;
  status: RegisterStatus;
  /** When it was written, so the list can order and the reader can recognise. */
  capturedAt: string;
  /** The last failure, so a queue that is not draining says why. */
  lastError: string | null;
}

/**
 * The store, or nothing.
 *
 * The thing that can be missing is `localStorage` itself, not `window`: a
 * server render has neither, but a browser with site data disabled -- and the
 * jsdom this file is tested under -- has a `window` whose `localStorage` is
 * undefined. Guarding on the outer object would have thrown in exactly the
 * situation the guard exists for.
 */
function store(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  /* Checked at runtime rather than asserted. The DOM types declare
     `window.localStorage` as always present, so a cast to `| undefined` reads
     as redundant to the type checker -- and is not, which is the whole point:
     a browser with site data disabled leaves it undefined and the first version
     of this function threw on the line that was supposed to guard it. */
  const candidate: unknown = window.localStorage;
  return typeof candidate === 'object' && candidate !== null && 'getItem' in candidate
    ? (candidate as Storage)
    : undefined;
}

export function readQueue(): QueuedObservation[] {
  const storage = store();
  if (!storage) return [];
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isQueued) : [];
  } catch {
    /* Unreadable storage is an empty queue, never a crash. Whatever was in
       there is already lost; taking the screen down with it would lose what
       the reader is about to write as well. */
    return [];
  }
}

export function writeQueue(queue: readonly QueuedObservation[]): boolean {
  const storage = store();
  if (!storage) return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(queue));
    return true;
  } catch {
    /* Quota, or a browser refusing storage. Reported rather than swallowed:
       the capture screen tells the writer their note is not being kept, which
       is the only honest thing to do about it. */
    return false;
  }
}

export function enqueue(observation: QueuedObservation): boolean {
  return writeQueue([...readQueue(), observation]);
}

export function removeFromQueue(clientRef: string): boolean {
  return writeQueue(readQueue().filter((entry) => entry.clientRef !== clientRef));
}

export function recordFailure(clientRef: string, message: string): boolean {
  return writeQueue(
    readQueue().map((entry) =>
      entry.clientRef === clientRef ? { ...entry, lastError: message } : entry,
    ),
  );
}

/**
 * An identifier no other device will produce.
 *
 * `crypto.randomUUID` where it exists, which is everywhere this application
 * runs -- it needs a secure context, and so does the application. The fallback
 * is for the test renderer rather than for a browser.
 */
export function newClientRef(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `capture-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isQueued(value: unknown): value is QueuedObservation {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  /* Only the fields the sender cannot do without. A stored entry from an older
     version of this screen is worth keeping and sending, not discarding for
     lacking a field that was added later. */
  return (
    typeof entry.clientRef === 'string' &&
    typeof entry.projectId === 'string' &&
    typeof entry.title === 'string'
  );
}
